import React from 'react';
import styled from '@emotion/styled';

import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material';
import { StaticPill } from '../../atoms/static-pill/static-pill';

import { IDeck } from '@interfaces/ship';
import { __ } from '../../../utils/translation-service';
import { StyledHeading, StyledDeckPlanContainer } from './deck-plan.style';
import ContentLoader from 'react-content-loader';
import { getAssetsPrefix } from '../../../utils/buildVersion';
import { NextImage } from '../../atoms/image/image';

export interface IDeckPlan {
    title: string;
    legendUrl?: string;
    decks: IDeck[];
}

const StyledStaticPill = styled(StaticPill)`
    border: 2px solid #f0f0f0;
    border-radius: 32px;
    box-shadow: 0px 6px 8px rgba(0, 0, 0, 0.02);
    cursor: pointer;
`;

const ImageLoader = (props: any) => {
    return (
        <ContentLoader
            viewBox="0 0 500 350"
            width={'100%'}
            height={300}
            backgroundColor="#fff"
            foregroundColor="#f3f3f3"
            id="deck-plan-loader"
            {...props}
        >
            <path d="M484.52,64.61H15.65C7.1,64.61.17,71.2.17,79.31V299.82c0,8.12,6.93,14.7,15.48,14.7H484.52c8.55,0,15.48-6.58,15.48-14.7V79.31C500,71.2,493.07,64.61,484.52,64.61Zm-9,204.34c0,11.84-7.14,21.44-15.94,21.44H436.39L359.16,171.52c-7.1-10.92-19.67-11.16-27-.51L258.64,277.94C253.78,285,245.73,286,240,280.2l-79.75-80.62c-6-6.06-14.33-5.7-20,.88L62.34,290.39H40.63c-8.8,0-15.94-9.6-15.94-21.44V110.19c0-11.84,7.14-21.44,15.94-21.44H459.54c8.8,0,15.94,9.6,15.94,21.44Z" />
            <ellipse cx="120" cy="140" rx="28" ry="28" />
        </ContentLoader>
    );
};

export const DeckPlan = (props: IDeckPlan) => {
    const [isLoadingImage, setIsLoadingImage] = React.useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const deckNumbersMap = props.decks.reduce(
        (acc, deck) => {
            acc[deck.number] = deck;
            return acc;
        },
        {} as { [key: string]: IDeck },
    );
    const deckNumbers = Object.keys(deckNumbersMap).sort((a, b) => {
        return Number(a) - Number(b);
    });
    const assetPrefix = getAssetsPrefix();
    const cabinFallbackBackground = `${assetPrefix}/static/images/no-deckplan-available.png`;
    const [selectedDeckNumber, setSelectedDeckNumber] = React.useState(deckNumbers[0] || '');

    React.useEffect(() => {
        setIsLoadingImage(true);
    }, [selectedDeckNumber]);

    const selectedDeckData = deckNumbersMap[selectedDeckNumber] || {};

    const mobileDeckImage = selectedDeckData.verticalDeckPlan?.url
        ? selectedDeckData.verticalDeckPlan
        : null;

    const deckImage = isMobile ? mobileDeckImage : selectedDeckData.deckPlan;

    return (
        <Container>
            <StyledDeckPlanContainer>
                <StyledHeading>{props.title}</StyledHeading>

                <Box
                    sx={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap',
                        marginBottom: '36px',
                    }}
                >
                    {deckNumbers.map((deckNumber) => (
                        <StyledStaticPill
                            key={deckNumber}
                            onClick={() => setSelectedDeckNumber(deckNumber)}
                            type={selectedDeckNumber === deckNumber ? 'primary' : undefined}
                        >
                            {deckNumbersMap[deckNumber].title}
                        </StyledStaticPill>
                    ))}
                </Box>

                <Box
                    sx={{
                        marginTop: '36px',
                        marginBottom: '36px',
                        textAlign: 'center',
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '6px',
                    }}
                >
                    {isLoadingImage && <ImageLoader />}
                    <NextImage
                        style={{
                            // need to use visibilty cause with display prop browser is not loadin the image
                            visibility: isLoadingImage ? 'hidden' : 'visible',
                            height: isLoadingImage ? 0 : '100%',
                        }}
                        imgUrl={(deckImage?.url as string) || cabinFallbackBackground}
                        alt="deck plan"
                        onLoad={() => setIsLoadingImage(false)}
                        placeholder={cabinFallbackBackground}
                    />
                </Box>
            </StyledDeckPlanContainer>
        </Container>
    );
};
